import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, X, ArrowRight, Zap, Star, Menu } from "lucide-react";

const Logo = ({ onClick }) => (
  <a href="/" onClick={onClick} className="inline-flex items-baseline cursor-pointer hover:opacity-80 transition-opacity">
    <span className="text-xl font-black text-[#0F172A] font-['Outfit'] tracking-tight">ORDO AI</span>
  </a>
);

const BASIC_FEATURES = [
  { text: "Weekly AI score across 11 platforms" },
  { text: "Full weekly score report" },
  { text: "GEO and SEO visibility tracking" },
  { text: "Action plan — you execute" },
  { text: "Score history and competitor leaderboard" },
  { text: "Vendor and client discovery list",     note: "You reach out yourself" },
  { text: "Outreach list with templates",          note: "You publish yourself" },
  { text: "PR pipeline action plan",               note: "You submit yourself" },
  { text: "Monthly GEO visibility report" },
];

const GROWTH_ADDITIONS = [
  { text: "Bot executes parts of your action plan" },
  { text: "GEO and SEO automatically optimized for you" },
  { text: "We handle all outreach and citations" },
  { text: "Vendor and client discovery",           note: "We send your contact directly" },
  { text: "Lead gen fully automated" },
  { text: "PR pipeline fully managed",             note: "You get the live link" },
  { text: "Monthly done-for-you activity report" },
  { text: "Live GEO Looker Studio dashboard" },
  { text: "Priority support" },
];

const COMPARISON = [
  { feature: "Weekly AI score (11 platforms)",      basic: true,               growth: true },
  { feature: "Full weekly report",                  basic: true,               growth: true },
  { feature: "GEO and SEO tracking",                basic: true,               growth: true },
  { feature: "Action plan",                         basic: "You execute",      growth: "Bot executes" },
  { feature: "Score history and leaderboard",       basic: true,               growth: true },
  { feature: "GEO and SEO optimization",            basic: "Report only",      growth: "Automated" },
  { feature: "Vendor and client discovery",         basic: "You reach out",    growth: "We send your contact" },
  { feature: "Outreach and citations",              basic: "You publish",      growth: "We handle it all" },
  { feature: "Lead generation",                     basic: "List provided",    growth: "Fully automated" },
  { feature: "PR pipeline",                         basic: "Action plan only", growth: "Fully managed" },
  { feature: "GEO visibility report",               basic: "Monthly PDF",      growth: "Live dashboard" },
  { feature: "Bot automation",                      basic: false,              growth: true },
  { feature: "Monthly done-for-you report",         basic: false,              growth: true },
  { feature: "Priority support",                    basic: false,              growth: true },
];

const FAQS = [
  {
    q: "How does the 3-day free trial work?",
    a: "You get full access to your plan for 3 days. On day 4 your subscription begins. You can cancel any time during the trial to avoid being charged.",
  },
  {
    q: "Are payments refundable?",
    a: "All payments are final and non-refundable. Once a billing cycle begins after your 3-day free trial, no refunds will be issued for partial or unused periods.",
  },
  {
    q: "Can I use one account for multiple businesses?",
    a: "Each subscription is tied to a single business. If you are a franchise or enterprise with multiple locations, contact support@theordo.io to learn about enterprise subscriptions.",
  },
  {
    q: "What is the difference between Basic and Growth?",
    a: "Basic gives you the full intelligence layer — AI scores across 11 platforms, GEO and SEO tracking, reports, and action plans that you execute yourself. Growth deploys the ORDO AI agents to do the work for you — GEO and SEO optimization, outreach, citations, lead gen, and PR all handled automatically.",
  },
  {
    q: "What does ORDO AI actually do on my behalf?",
    a: "In Growth, ORDO AI contacts directories, publications, and potential clients on your behalf using pre-built ORDO AI systems. We scan and automate your GEO and SEO presence, build citations, publish optimized content, and send your business contact to potential clients. By subscribing, you authorize ORDO AI to act as your representative in these outreach activities.",
  },
  {
    q: "Can I upgrade from Basic to Growth?",
    a: "Yes, anytime. Your billing adjusts immediately and you get access to all Growth features right away.",
  },
];

const TERMS = `TERMS OF SERVICE — ORDO AI LLC
Last updated: February 2026

1. SERVICE DESCRIPTION
ORDO AI provides AI-powered business development services including AI search visibility scanning, GEO and SEO optimization, outreach and citation building, lead generation, and digital PR services.

2. SUBSCRIPTION & TRIAL
Each subscription begins with a 3-day free trial. After the trial period ends, your selected plan will be billed automatically.

3. NON-REFUNDABLE PAYMENTS
All payments are final and non-refundable once a billing cycle begins after your 3-day free trial.

4. ONE BUSINESS PER SUBSCRIPTION
Each ORDO AI subscription is tied to a single business.

5. AUTHORIZATION
By subscribing, you authorize ORDO AI to act as your representative in contacting directories, publications, and potential business contacts.

6. NO GUARANTEE OF RESULTS
ORDO AI does not guarantee specific ranking positions, lead volumes, or revenue outcomes.

7. GOVERNING LAW
These terms are governed by the laws of the State of Florida.

Contact: support@theordo.io`;

const PRIVACY = `PRIVACY POLICY — ORDO AI LLC
Last updated: February 2026

ORDO AI uses pre-built systems and data sources for AI visibility scanning, GEO and SEO optimization, business outreach, citation building, lead generation, and digital PR. Data is used solely to provide and improve ORDO AI services. We do not sell your data to third parties.

Contact: support@theordo.io`;

const PolicyModal = ({ title, onClose }) => {
  const content = title === "Terms of Service" ? TERMS : PRIVACY;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <h3 className="font-bold text-[#0F172A] font-['Outfit']">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-6 overflow-y-auto flex-1">
          <pre className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed font-sans">{content}</pre>
        </div>
      </div>
    </div>
  );
};

const Pricing = () => {
  const navigate = useNavigate();
  const [annual, setAnnual]             = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [policyModal, setPolicyModal]   = useState(null);
  const [openFaq, setOpenFaq]           = useState(null);

  // ── All CTAs scan first, then show results with plan pre-highlighted ──
  const handleStartTrial = (plan) => {
    localStorage.setItem("plan_intent", plan);
    navigate(`/?scan=1&plan=${plan}`);
  };

  return (
    <div className="min-h-screen bg-white font-['Outfit']">

      {/* NAV */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="hidden md:flex items-center gap-2">
            <a href="/pricing" className="text-sm font-medium text-slate-700 hover:text-[#1E40AF] px-3 py-2 transition-colors">Pricing</a>
            <a href="/login"   className="text-sm font-medium text-slate-700 hover:text-[#1E40AF] px-3 py-2 transition-colors">Log In</a>
            <button onClick={() => handleStartTrial("basic")}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors">
              Free ORDO AI Score
            </button>
          </div>
          <div className="md:hidden flex items-center gap-2">
            <button onClick={() => handleStartTrial("basic")}
              className="bg-[#F97316] text-white text-xs font-bold px-3 py-2 rounded-lg">
              Free Score
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-slate-600">
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 px-4 py-3 space-y-2">
            <a href="/" className="block text-sm font-medium text-slate-700 py-2">Home</a>
            <a href="/login" className="block text-sm font-medium text-slate-700 py-2">Log In</a>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="bg-white py-16 text-center border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 text-[#1E40AF] text-xs font-bold px-4 py-2 rounded-full mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-[#1E40AF] animate-pulse" />
            Your Business AI Developer
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-[#0F172A] font-['Outfit'] mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-slate-600 text-lg max-w-xl mx-auto mb-2">
            Deploy your AI agents today. Start your market domination
          </p>
          <p className="text-slate-500 text-sm">Scan your business free → choose a plan → start your trial</p>
        </div>
      </section>

      {/* PRICING CARDS */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">

          {/* Toggle */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <span className={`text-sm font-semibold ${!annual ? "text-[#0F172A]" : "text-slate-400"}`}>Monthly</span>
            <button onClick={() => setAnnual(!annual)}
              className="relative w-12 h-6 rounded-full transition-colors"
              style={{ background: annual ? "#1E40AF" : "#CBD5E1" }}>
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${annual ? "translate-x-7" : "translate-x-1"}`} />
            </button>
            <span className={`text-sm font-semibold ${annual ? "text-[#0F172A]" : "text-slate-400"}`}>
              Annual
              <span className="ml-2 text-xs font-bold px-2 py-0.5 rounded-full bg-[#10B981]/15 text-[#10B981]">Save 20%</span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* BASIC */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col hover:shadow-lg transition-shadow">
              <div className="h-1.5 bg-[#10B981]" />
              <div className="p-8">
                <h2 className="text-2xl font-black text-[#0F172A] font-['Outfit'] mb-1">Basic</h2>
                <p className="text-sm text-slate-500 mb-4">Scan, report, and act</p>
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 mb-6">
                  <p className="text-xs font-bold text-[#059669]">We automate $2,500 worth of business development for you</p>
                </div>
                <div className="flex items-end gap-2 mb-1">
                  {annual && <span className="text-xl line-through text-slate-400 font-semibold mb-1">$149</span>}
                  <span className="text-5xl font-black text-[#0F172A] font-['Outfit']">${annual ? 119 : 149}</span>
                  <span className="text-slate-500 text-sm mb-2">/mo</span>
                </div>
                {annual
                  ? <p className="text-xs text-slate-500 mb-6">Billed $1,430/year <span className="ml-1 text-[#10B981] font-semibold">Save $360/yr</span></p>
                  : <div className="mb-6" />
                }
                <button onClick={() => handleStartTrial("basic")}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white font-bold py-4 rounded-xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-105 mb-8">
                  <Zap className="w-4 h-4" /> Scan Free → Start Basic Trial <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <div className="px-8 pb-8 flex-1">
                <div className="bg-slate-50 rounded-xl p-5 space-y-3">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">What's included</p>
                  {BASIC_FEATURES.map((f, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <CheckCircle className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-sm text-slate-700">{f.text}</span>
                        {f.note && <span className="block text-xs text-[#10B981] font-medium mt-0.5">{f.note}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-center mt-5 text-slate-400">3 days free trial, no set up fees</p>
              </div>
            </div>

            {/* GROWTH */}
            <div className="bg-white rounded-2xl overflow-hidden flex flex-col hover:shadow-xl transition-shadow relative"
              style={{ border: "2px solid #1E40AF", boxShadow: "0 0 0 4px rgba(30,64,175,0.06)" }}>
              <div className="flex gap-2 absolute top-5 right-5 z-10">
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#1E40AF] text-white">Most Popular</span>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#10B981] text-white flex items-center gap-1">
                  <Star className="w-3 h-3" /> Best Value
                </span>
              </div>
              <div className="h-1.5 bg-[#1E40AF]" />
              <div className="p-8">
                <h2 className="text-2xl font-black text-[#0F172A] font-['Outfit'] mb-1">Growth</h2>
                <p className="text-sm text-slate-500 mb-4">We run it all. You collect the results</p>
                <div className="bg-[#EFF6FF] border border-blue-100 rounded-xl px-4 py-3 mb-6">
                  <p className="text-xs font-bold text-[#1E40AF]">We automate $10,000 worth of business development for you</p>
                </div>
                <div className="flex items-end gap-2 mb-1">
                  {annual && <span className="text-xl line-through text-slate-400 font-semibold mb-1">$499</span>}
                  <span className="text-5xl font-black text-[#0F172A] font-['Outfit']">${annual ? 399 : 499}</span>
                  <span className="text-slate-500 text-sm mb-2">/mo</span>
                </div>
                {annual
                  ? <p className="text-xs text-slate-500 mb-6">Billed $4,790/year <span className="ml-1 text-[#10B981] font-semibold">Save $1,200/yr</span></p>
                  : <div className="mb-6" />
                }
                <button onClick={() => handleStartTrial("growth")}
                  className="w-full bg-[#1E40AF] hover:bg-[#1E3A8A] text-white font-bold py-4 rounded-xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-105 mb-8">
                  <Zap className="w-4 h-4" /> Scan Free → Start Growth Trial <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              <div className="px-8 pb-8 flex-1">
                <div className="rounded-xl p-5 space-y-3" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
                  <p className="text-xs font-bold text-[#1E40AF] uppercase tracking-wider mb-1">Everything in Basic, plus:</p>
                  {GROWTH_ADDITIONS.map((f, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <CheckCircle className="w-4 h-4 text-[#1E40AF] flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-sm text-slate-700">{f.text}</span>
                        {f.note && <span className="block text-xs text-[#1E40AF] font-medium mt-0.5">{f.note}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-center mt-5 text-slate-400">3 days free trial, no set up fees</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-16 bg-[#F8FAFC] border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl font-black text-[#0F172A] text-center mb-10 font-['Outfit']">Full Plan Comparison</h2>
          <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
            <div className="grid grid-cols-3 bg-[#EFF6FF]">
              <div className="p-4 text-sm font-bold text-slate-500">Feature</div>
              <div className="p-4 text-sm font-bold text-[#10B981] text-center border-l border-slate-200">Basic</div>
              <div className="p-4 text-sm font-bold text-[#1E40AF] text-center border-l border-slate-200">Growth</div>
            </div>
            {COMPARISON.map((row, i) => (
              <div key={i} className="grid grid-cols-3 border-t border-slate-100"
                style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                <div className="p-4 text-sm text-slate-700">{row.feature}</div>
                <div className="p-4 text-center border-l border-slate-100">
                  {row.basic === true  ? <CheckCircle className="w-4 h-4 text-[#10B981] mx-auto" /> :
                   row.basic === false ? <X className="w-4 h-4 text-slate-300 mx-auto" /> :
                   <span className="text-xs text-slate-500">{row.basic}</span>}
                </div>
                <div className="p-4 text-center border-l border-slate-100">
                  {row.growth === true  ? <CheckCircle className="w-4 h-4 text-[#1E40AF] mx-auto" /> :
                   row.growth === false ? <X className="w-4 h-4 text-slate-300 mx-auto" /> :
                   <span className="text-xs font-semibold text-[#1E40AF]">{row.growth}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE */}
      <section className="py-16 bg-[#EFF6FF] border-t border-blue-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs font-bold text-[#1E40AF] uppercase tracking-widest mb-4">The Value Equation</p>
          <h2 className="text-3xl md:text-4xl font-black text-[#0F172A] font-['Outfit'] mb-4">
            We Automate <span className="text-[#EF4444]">$10,000</span> Worth of Business Development For Only <span className="text-[#10B981]">$499/mo</span>
          </h2>
          <p className="text-slate-600 text-sm leading-relaxed max-w-xl mx-auto">
            Outreach. Citations. Lead generation. PR. AI search ranking. A full agency does this for $8,000–$15,000/month. ORDO AI deploys AI agents that do the same but automatically, every single day
          </p>
          <div className="grid grid-cols-3 gap-4 mt-8 max-w-lg mx-auto">
            {[
              { label: "Traditional Agency", cost: "$8,000–$15,000/mo", color: "#EF4444" },
              { label: "Freelance Team",      cost: "$3,000–$6,000/mo",  color: "#F97316" },
              { label: "ORDO AI Growth",      cost: "$499/mo",           color: "#10B981" },
            ].map((item, i) => (
              <div key={i} className="rounded-xl border border-blue-100 p-4 bg-white shadow-sm">
                <div className="text-xs text-slate-500 mb-1">{item.label}</div>
                <div className="text-sm font-black font-['Outfit']" style={{ color: item.color }}>{item.cost}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-2xl font-black text-[#0F172A] text-center mb-10 font-['Outfit']">Common Questions</h2>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="rounded-xl border border-slate-200 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors">
                  <span className="font-semibold text-[#0F172A] text-sm pr-4">{faq.q}</span>
                  <span className="text-slate-400 flex-shrink-0 text-lg font-light">{openFaq === i ? "−" : "+"}</span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 border-t border-slate-100">
                    <p className="text-slate-600 text-sm leading-relaxed pt-3">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="py-16 bg-[#EFF6FF] border-t border-blue-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-black text-[#0F172A] font-['Outfit'] mb-3">Ready to Dominate Your Market?</h2>
          <p className="text-slate-600 mb-8">Scan your business free. Choose your plan. Deploy your AI agents</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => handleStartTrial("basic")}
              className="border-2 border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white font-bold px-8 py-4 rounded-xl text-sm transition-all">
              Start Basic — $149/mo
            </button>
            <button onClick={() => handleStartTrial("growth")}
              className="bg-[#10B981] hover:bg-[#059669] text-white font-bold px-8 py-4 rounded-xl text-sm flex items-center justify-center gap-2 transition-all hover:scale-105 shadow-lg shadow-green-100">
              <Star className="w-4 h-4" /> Start Growth — $499/mo
            </button>
          </div>
          <p className="text-slate-500 text-xs mt-4">Scan first, then choose your plan · 3 days free trial · No setup fees</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white border-t border-slate-200 py-6">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo />
            <span className="text-slate-500 text-xs">Your Business AI Developer</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-5 text-xs text-slate-500">
            <button onClick={() => setPolicyModal("Terms of Service")} className="hover:text-[#1E40AF] transition-colors">Terms of Service</button>
            <button onClick={() => setPolicyModal("Privacy Policy")} className="hover:text-[#1E40AF] transition-colors">Privacy Policy</button>
            <a href="mailto:support@theordo.io" className="hover:text-[#1E40AF] transition-colors">support@theordo.io</a>
          </div>
          <p className="text-xs text-slate-500">© 2026 ORDO AI LLC</p>
        </div>
      </footer>

      {policyModal && <PolicyModal title={policyModal} onClose={() => setPolicyModal(null)} />}
    </div>
  );
};

export default Pricing;
